import { KeyRound, ShieldCheck, ShieldOff, User } from 'lucide-react';
import type { ActorSummary, AuthorityResponse } from '../lib/api';

/**
 * Identity and authority.
 *
 * The shape of this view is the argument: one human at the root, agents nested beneath
 * him. An agent is never drawn as a peer account with a role, because it does not have
 * one — it has a key, a delegation, and an attributable history.
 *
 * Three things are deliberately kept apart, since collapsing them is how a system
 * starts granting authority by accident:
 *   identity            who this actor is, proven by its own key
 *   project membership  what context it works in, which grants nothing
 *   delegation          what it is currently permitted to do, bound to evidence
 */
export function AuthorityPanel({ authority }: { authority: AuthorityResponse }) {
  return (
    <div className="reading-container">
      <header className="doc-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }} aria-hidden="true">
            🛡️
          </span>
          Identity &amp; Authority
        </h1>
        <p className="text-small" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          One human principal authenticates interactively through Okta. Every agent below holds its
          own locally generated key pair; Atlas stores only the public half.
        </p>
      </header>

      <article className="markdown-body">
        <div className="identity-root">
          <div className="human-principal">
            <div className="principal-avatar" aria-hidden="true">
              {authority.principal.displayName.slice(0, 1).toUpperCase()}
            </div>
            <div className="principal-details">
              <h3>{authority.principal.displayName}</h3>
              <span>
                <User size={12} style={{ verticalAlign: -2 }} /> Human principal · authenticates via{' '}
                {authority.principal.authentication}
              </span>
            </div>
          </div>

          <div className="agent-hierarchy">
            {authority.actors.length === 0 ? (
              <p className="text-small">No agent identities are enrolled beneath this principal yet.</p>
            ) : (
              authority.actors.map((actor) => <AgentCard key={actor.id} actor={actor} />)
            )}
          </div>
        </div>
      </article>
    </div>
  );
}

function AgentCard({ actor }: { actor: ActorSummary }) {
  const revoked = actor.lifecycle === 'revoked' || actor.lifecycle === 'suspended';

  return (
    <div className={`agent-actor${revoked ? ' revoked-actor' : ' active-actor'}`}>
      <div className="agent-header">
        <div className="agent-title">
          <span className="agent-icon" aria-hidden="true">
            {revoked ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
          </span>
          {actor.label}
          <span className="badge" style={{ background: 'var(--bg-code)' }}>
            kind={actor.kind}
          </span>
          <span className={`badge ${revoked ? 'status-error' : 'status-latest'}`}>
            {revoked ? actor.lifecycle : 'Enrolled & active'}
          </span>
        </div>
      </div>

      <div className="agent-body">
        <div className="key-field">
          <span className="field-label">
            <KeyRound size={11} style={{ verticalAlign: -1 }} /> Public JWK fingerprint
            {revoked ? ' (historical)' : ''}
          </span>
          {/*
            The fingerprint is shown in full and never truncated: an operator comparing
            it against what an agent reports needs the whole value. It is public
            verification material -- there is no private key anywhere in this view,
            and nothing here ever asks for one.
          */}
          <code className="fingerprint">
            {actor.algorithm} · {actor.fingerprint}
          </code>
          {actor.retiredFingerprints.length > 0 ? (
            <span className="text-small" style={{ color: 'var(--text-muted)' }}>
              {actor.retiredFingerprints.length} retired key
              {actor.retiredFingerprints.length === 1 ? '' : 's'} retained so historical signatures
              stay verifiable
            </span>
          ) : null}
        </div>

        <div className="agent-distinction-row" style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <div className="delegation-evidence" style={{ flex: 1, ...(revoked ? { opacity: 0.5 } : {}) }}>
            <strong>Current authority / delegation:</strong>
            <br />
            {revoked ? 'None — new challenges and tokens are refused.' : 'Evidence-bound, task-scoped.'}
          </div>
          <div
            className="membership-evidence"
            style={{
              flex: 1,
              padding: '8px 12px',
              background: '#e0f2fe',
              borderLeft: '3px solid #0284c7',
              color: '#075985',
              fontSize: 12,
              borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
              ...(revoked ? { opacity: 0.5 } : {}),
            }}
          >
            <strong>Project membership:</strong>
            <br />
            Context only — carries no authority.
          </div>
        </div>

        <div className="lifecycle-timeline">
          <div className="timeline-event success">
            <strong>Enrolled:</strong> {new Date(actor.enrolledAt).toISOString()} (public key only)
          </div>
          {actor.rotatedAt ? (
            <div className="timeline-event success">
              <strong>Key rotation:</strong> {new Date(actor.rotatedAt).toISOString()} — proved possession
              of both keys; identity and history preserved
            </div>
          ) : null}
          {actor.revokedAt ? (
            <div className="timeline-event error">
              <strong>Revoked:</strong> {new Date(actor.revokedAt).toISOString()} — new challenges rejected;
              prior revisions keep their attribution
            </div>
          ) : null}
        </div>

        <div
          className="knowledge-contribution"
          style={{ marginTop: 8, borderTop: '1px dashed var(--border)', paddingTop: 8 }}
        >
          <span className="field-label">Credential subject</span>
          <code className="fingerprint">{actor.credentialSubject}</code>
          <p className="text-small" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
            Identity follows this subject, not the display label. Renaming an agent never creates a
            second actor, and two agents sharing a label share no authority.
          </p>
        </div>
      </div>
    </div>
  );
}
